import React from "react";
import { Link } from "react-router-dom";
import kanaLogo from "./kana_mascot_white_inside.png";
import kanaShot1 from "./screenshots/kanatomo-1.png";
import kanaShot2 from "./screenshots/kanatomo-2.png";
import { trackStoreClick } from "./firebaseAnalytics";

type Locale = "ko" | "en" | "ja" | "es" | "fr" | "pt_BR" | "vi";

const LOCALE_STORAGE_KEY = "kanatomo-landing-locale";

const localeLabels: Record<Locale, string> = {
  ko: "KO",
  en: "EN",
  ja: "JA",
  es: "ES",
  fr: "FR",
  pt_BR: "PT",
  vi: "VI",
};

const content: Record<
  Locale,
  {
    appName: string;
    navAbout: string;
    navTerms: string;
    navPrivacy: string;
    overline: string;
    title: string[];
    lead: string;
    appStore: string;
    googlePlay: string;
    note: string;
    visualBody: string;
    cards: Array<{ eyebrow: string; title: string; body: string }>;
    footerAbout: string;
    footerTerms: string;
    footerPrivacy: string;
  }
> = {
  ko: {
    appName: "KanaTomo",
    navAbout: "앱 소개",
    navTerms: "이용약관",
    navPrivacy: "개인정보 처리방침",
    overline: "Now on iPhone and Android",
    title: ["예문과 함께 익히는", "조용한 일본어 학습"],
    lead:
      "JLPT N1-N5 단어를 예문, 발음, 퀴즈, 복습 흐름 안에서 차분하게 익히는 일본어 학습 앱. 단순 암기보다 문맥과 반복의 리듬으로 오래 남는 학습 경험을 만듭니다.",
    appStore: "App Store에서 보기",
    googlePlay: "Google Play에서 보기",
    note:
      "회원가입 없이 바로 사용할 수 있으며, 학습 기록은 기기 중심으로 저장됩니다. 안정성 개선과 광고 제공을 위한 일부 외부 서비스 처리는 개인정보 처리방침에서 확인할 수 있습니다.",
    visualBody:
      "단어, 예문, 발음을 한 흐름으로 묶어 오늘의 학습을 짧고 선명하게 이어줍니다. 레벨별 학습과 퀴즈, 복습 루프가 자연스럽게 맞물립니다.",
    cards: [
      {
        eyebrow: "JLPT",
        title: "N1부터 N5까지 체계적인 단어 학습",
        body: "공식 등급 단어를 기준으로 레벨별 학습 흐름을 구성해, 필요한 단계부터 차근차근 쌓아갈 수 있습니다.",
      },
      {
        eyebrow: "Context",
        title: "예문 중심으로 기억에 남는 학습",
        body: "단어만 따로 외우지 않고 자연스러운 예문 안에서 뜻과 쓰임을 함께 익혀 실제 읽기와 회화 감각으로 이어집니다.",
      },
      {
        eyebrow: "Review",
        title: "짧게 열고 이어서 복습하는 루프",
        body: "퀴즈와 반복 복습 흐름을 통해 매일 짧은 시간만 써도 학습이 끊기지 않도록 설계했습니다.",
      },
    ],
    footerAbout: "상세 소개",
    footerTerms: "이용약관",
    footerPrivacy: "개인정보 처리방침",
  },
  en: {
    appName: "KanaTomo",
    navAbout: "About",
    navTerms: "Terms",
    navPrivacy: "Privacy Policy",
    overline: "Now on iPhone and Android",
    title: ["Calm Japanese study", "built around examples"],
    lead:
      "A Japanese learning app built around JLPT N1-N5 vocabulary, example sentences, audio, quizzes, and review loops. It focuses on context and repetition rather than memorization alone.",
    appStore: "View on the App Store",
    googlePlay: "View on Google Play",
    note:
      "No account required. Study history is stored primarily on your device. Some external services may be used for stability and ads, as described in the privacy policy.",
    visualBody:
      "Words, example sentences, and pronunciation are connected in one quiet study flow. Level-based learning and review loops help you keep momentum every day.",
    cards: [
      {
        eyebrow: "JLPT",
        title: "Structured vocabulary from N1 to N5",
        body: "Learn from official JLPT levels so you can build your pace step by step, starting from the level that fits you best.",
      },
      {
        eyebrow: "Context",
        title: "Remember words through example sentences",
        body: "Instead of isolated memorization, KanaTomo helps words stay with you through natural context and usage.",
      },
      {
        eyebrow: "Review",
        title: "Short sessions, steady review loops",
        body: "Quizzes and review cycles are designed so even a short daily session keeps your learning going.",
      },
    ],
    footerAbout: "Detailed About",
    footerTerms: "Terms",
    footerPrivacy: "Privacy Policy",
  },
  ja: {
    appName: "KanaTomo",
    navAbout: "アプリ紹介",
    navTerms: "利用規約",
    navPrivacy: "プライバシーポリシー",
    overline: "Now on iPhone and Android",
    title: ["例文と一緒に身につける", "静かな日本語学習"],
    lead:
      "JLPT N1-N5 の単語を、例文・音声・クイズ・復習の流れの中で落ち着いて学べる日本語学習アプリです。暗記だけでなく、文脈と反復のリズムを大切にしています。",
    appStore: "App Storeで見る",
    googlePlay: "Google Playで見る",
    note:
      "アカウント登録は不要です。学習記録は主に端末内に保存されます。安定性向上や広告提供のための外部サービス利用についてはプライバシーポリシーをご確認ください。",
    visualBody:
      "単語・例文・発音をひとつの学習の流れにまとめ、毎日の学習を短くてもはっきり続けられるように設計しています。",
    cards: [
      {
        eyebrow: "JLPT",
        title: "N1 から N5 まで体系的な単語学習",
        body: "公式レベルの単語を基準にしているため、自分に合った段階から無理なく積み上げられます。",
      },
      {
        eyebrow: "Context",
        title: "例文中心で記憶に残る学習",
        body: "単語だけを切り離して覚えるのではなく、自然な例文の中で意味と使い方を一緒に身につけられます。",
      },
      {
        eyebrow: "Review",
        title: "短く開いて続けられる復習ループ",
        body: "クイズと反復復習の流れにより、短い学習時間でも習慣が途切れないように設計されています。",
      },
    ],
    footerAbout: "詳しい紹介",
    footerTerms: "利用規約",
    footerPrivacy: "プライバシーポリシー",
  },
  es: {
    appName: "KanaTomo",
    navAbout: "Sobre la app",
    navTerms: "Términos",
    navPrivacy: "Política de privacidad",
    overline: "Now on iPhone and Android",
    title: ["Estudio de japonés tranquilo", "a través de ejemplos"],
    lead:
      "Una app de aprendizaje de japonés centrada en vocabulario JLPT N1-N5, frases de ejemplo, audio, cuestionarios y repasos. Prioriza el contexto y la repetición por encima de la memorización aislada.",
    appStore: "Ver en el App Store",
    googlePlay: "Ver en Google Play",
    note:
      "No se requiere cuenta. El historial de estudio se guarda principalmente en tu dispositivo. Consulta la política de privacidad para conocer los servicios externos usados por estabilidad o anuncios.",
    visualBody:
      "Palabras, frases de ejemplo y pronunciación se conectan en un flujo de estudio claro y sereno para el aprendizaje diario.",
    cards: [
      { eyebrow: "JLPT", title: "Vocabulario estructurado de N1 a N5", body: "Aprende con el marco oficial del JLPT y avanza paso a paso desde el nivel que mejor se ajuste a ti." },
      { eyebrow: "Context", title: "Memoriza palabras con frases de ejemplo", body: "KanaTomo ayuda a retener vocabulario a través del uso real y el contexto, no solo con memorización aislada." },
      { eyebrow: "Review", title: "Sesiones cortas, repaso constante", body: "Los cuestionarios y ciclos de repaso están pensados para que incluso sesiones breves mantengan tu progreso." },
    ],
    footerAbout: "Más información",
    footerTerms: "Términos",
    footerPrivacy: "Política de privacidad",
  },
  fr: {
    appName: "KanaTomo",
    navAbout: "À propos",
    navTerms: "Conditions",
    navPrivacy: "Politique de confidentialité",
    overline: "Now on iPhone and Android",
    title: ["Un apprentissage du japonais", "calme et contextuel"],
    lead:
      "Une application de japonais construite autour du vocabulaire JLPT N1-N5, des exemples, de l'audio, des quiz et des révisions. Elle privilégie le contexte et la répétition plutôt que la mémorisation seule.",
    appStore: "Voir sur l'App Store",
    googlePlay: "Voir sur Google Play",
    note:
      "Aucun compte requis. L'historique d'étude est principalement conservé sur votre appareil. Consultez la politique de confidentialité pour les services externes liés à la stabilité ou à la publicité.",
    visualBody:
      "Mots, exemples et prononciation se rejoignent dans un flux d'étude clair et apaisé pour rendre l'apprentissage quotidien plus régulier.",
    cards: [
      { eyebrow: "JLPT", title: "Un vocabulaire structuré du N1 au N5", body: "Appuyez-vous sur les niveaux officiels du JLPT et progressez étape par étape depuis le niveau qui vous convient." },
      { eyebrow: "Context", title: "Retenir les mots grâce aux exemples", body: "KanaTomo aide les mots à rester en mémoire grâce à l'usage réel et au contexte, pas seulement à la mémorisation isolée." },
      { eyebrow: "Review", title: "Courtes sessions, révision continue", body: "Les quiz et les boucles de révision sont pensés pour maintenir l'élan même avec peu de temps chaque jour." },
    ],
    footerAbout: "En savoir plus",
    footerTerms: "Conditions",
    footerPrivacy: "Politique de confidentialité",
  },
  pt_BR: {
    appName: "KanaTomo",
    navAbout: "Sobre o app",
    navTerms: "Termos",
    navPrivacy: "Política de privacidade",
    overline: "Now on iPhone and Android",
    title: ["Estudo de japonês calmo", "guiado por exemplos"],
    lead:
      "Um app de aprendizado de japonês baseado em vocabulário JLPT N1-N5, frases de exemplo, áudio, quizzes e revisão. Ele prioriza contexto e repetição em vez de memorização isolada.",
    appStore: "Ver na App Store",
    googlePlay: "Ver no Google Play",
    note:
      "Nenhuma conta necessária. O histórico de estudo fica principalmente no seu dispositivo. Veja a política de privacidade para detalhes sobre serviços externos usados por estabilidade ou anúncios.",
    visualBody:
      "Palavras, frases de exemplo e pronúncia se unem em um fluxo de estudo tranquilo e consistente para o aprendizado do dia a dia.",
    cards: [
      { eyebrow: "JLPT", title: "Vocabulário estruturado do N1 ao N5", body: "Aprenda usando os níveis oficiais do JLPT e avance passo a passo a partir do nível ideal para você." },
      { eyebrow: "Context", title: "Memorize palavras com exemplos", body: "KanaTomo ajuda as palavras a permanecerem através do uso real e do contexto, não só da memorização isolada." },
      { eyebrow: "Review", title: "Sessões curtas, revisão constante", body: "Quizzes e ciclos de revisão foram pensados para manter o ritmo mesmo com pouco tempo por dia." },
    ],
    footerAbout: "Saiba mais",
    footerTerms: "Termos",
    footerPrivacy: "Política de privacidade",
  },
  vi: {
    appName: "KanaTomo",
    navAbout: "Giới thiệu",
    navTerms: "Điều khoản",
    navPrivacy: "Chính sách bảo mật",
    overline: "Now on iPhone and Android",
    title: ["Học tiếng Nhật yên tĩnh", "qua ví dụ thực tế"],
    lead:
      "Ứng dụng học tiếng Nhật xoay quanh từ vựng JLPT N1-N5, câu ví dụ, âm thanh, câu hỏi nhanh và ôn tập. Ứng dụng ưu tiên ngữ cảnh và nhịp lặp lại hơn là học thuộc lòng đơn lẻ.",
    appStore: "Xem trên App Store",
    googlePlay: "Xem trên Google Play",
    note:
      "Không cần tài khoản. Lịch sử học tập được lưu chủ yếu trên thiết bị của bạn. Xem chính sách bảo mật để biết thêm về các dịch vụ ngoài dùng cho độ ổn định hoặc quảng cáo.",
    visualBody:
      "Từ vựng, câu ví dụ và phát âm được nối thành một luồng học tập rõ ràng và yên tĩnh để việc học hàng ngày bền hơn.",
    cards: [
      { eyebrow: "JLPT", title: "Từ vựng có cấu trúc từ N1 đến N5", body: "Học theo khung JLPT chính thức để tiến từng bước từ cấp độ phù hợp nhất với bạn." },
      { eyebrow: "Context", title: "Ghi nhớ từ qua câu ví dụ", body: "KanaTomo giúp từ ở lại lâu hơn nhờ ngữ cảnh và cách dùng thực tế, không chỉ bằng ghi nhớ rời rạc." },
      { eyebrow: "Review", title: "Học ngắn, ôn đều", body: "Quiz và vòng lặp ôn tập được thiết kế để ngay cả một khoảng thời gian ngắn mỗi ngày vẫn giữ được đà học." },
    ],
    footerAbout: "Xem thêm",
    footerTerms: "Điều khoản",
    footerPrivacy: "Chính sách bảo mật",
  },
};

const detectLocale = (): Locale => {
  if (typeof window === "undefined") {
    return "en";
  }

  const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored && Object.keys(localeLabels).includes(stored)) {
    return stored as Locale;
  }

  const browserLocales = window.navigator.languages?.length
    ? window.navigator.languages
    : [window.navigator.language];

  for (const locale of browserLocales) {
    const normalized = locale.toLowerCase();
    if (normalized.startsWith("ko")) return "ko";
    if (normalized.startsWith("ja")) return "ja";
    if (normalized.startsWith("es")) return "es";
    if (normalized.startsWith("fr")) return "fr";
    if (normalized.startsWith("pt")) return "pt_BR";
    if (normalized.startsWith("vi")) return "vi";
    if (normalized.startsWith("en")) return "en";
  }

  return "en";
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Apple SD Gothic Neo', 'Noto Sans KR', 'Noto Sans JP', 'Noto Sans', Arial, sans-serif",
    color: "var(--ink)",
    background: "linear-gradient(180deg, #e7f2ff 0%, #ffffff 34%)",
  },
  shell: {
    width: "100%",
    maxWidth: 1120,
    margin: "0 auto",
    padding: "28px 20px 64px",
    boxSizing: "border-box",
  },
  nav: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    marginBottom: 32,
    flexWrap: "wrap",
  },
  navBrand: {
    color: "var(--kana)",
    textDecoration: "none",
    fontSize: 14,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    fontWeight: 700,
  },
  navActions: {
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
    alignItems: "center",
  },
  navLinks: {
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
    alignItems: "center",
    fontSize: 14,
  },
  navLink: {
    color: "var(--ink-2)",
    textDecoration: "none",
  },
  localeSwitch: {
    display: "inline-flex",
    gap: 6,
    padding: 4,
    borderRadius: 999,
    background: "rgba(28,24,56,0.05)",
    border: "1px solid var(--rule-soft)",
  },
  localeButton: {
    minWidth: 42,
    padding: "8px 10px",
    borderRadius: 999,
    border: "none",
    background: "transparent",
    color: "var(--ink-3)",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  activeLocaleButton: {
    background: "var(--kana)",
    color: "#fff",
    boxShadow: "0 6px 18px rgba(30, 136, 247, 0.3)",
  },
  hero: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: 28,
    alignItems: "center",
    padding: "8px 0 28px",
  },
  heroCopy: {
    textAlign: "left",
  },
  overline: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    borderRadius: 999,
    background: "rgba(30, 136, 247, 0.1)",
    border: "1px solid rgba(30, 136, 247, 0.18)",
    color: "var(--kana)",
    fontSize: 12,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    fontWeight: 700,
  },
  title: {
    margin: "18px 0 14px",
    fontSize: "clamp(2.3rem, 6vw, 4.2rem)",
    lineHeight: 1.06,
    letterSpacing: "-0.03em",
    fontWeight: 800,
    color: "var(--ink)",
  },
  lead: {
    margin: "0 0 18px",
    fontSize: 16,
    lineHeight: 1.8,
    color: "var(--ink-2)",
    maxWidth: 560,
  },
  buttonRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 24,
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 50,
    padding: "14px 20px",
    borderRadius: 999,
    background: "var(--kana)",
    color: "#fff",
    textDecoration: "none",
    fontSize: 15,
    fontWeight: 700,
    boxShadow: "0 14px 30px rgba(30, 136, 247, 0.3)",
    flex: "1 1 180px",
  },
  secondaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 50,
    padding: "14px 20px",
    borderRadius: 999,
    background: "#fff",
    border: "1.5px solid var(--rule)",
    color: "var(--ink)",
    textDecoration: "none",
    fontSize: 15,
    fontWeight: 700,
    boxShadow: "0 14px 30px rgba(28,24,56,0.08)",
    flex: "1 1 180px",
  },
  note: {
    marginTop: 16,
    fontSize: 13,
    lineHeight: 1.7,
    color: "var(--ink-3)",
  },
  heroVisualWrap: {
    position: "relative",
    minHeight: 460,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  halo: {
    position: "absolute",
    width: 360,
    height: 360,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(30, 136, 247, 0.18) 0%, rgba(30, 136, 247, 0.06) 42%, rgba(30, 136, 247, 0) 74%)",
    filter: "blur(4px)",
  },
  heroCard: {
    position: "relative",
    width: "min(100%, 430px)",
    padding: "28px 24px",
    borderRadius: 28,
    background: "linear-gradient(180deg, #ffffff 0%, #f4f7fb 100%)",
    border: "1px solid var(--rule-soft)",
    boxShadow: "0 20px 60px rgba(28,24,56,0.14)",
    overflow: "hidden",
  },
  stars: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "radial-gradient(circle at 14% 18%, rgba(59,120,184,0.26) 0 1.5px, transparent 1.6px), radial-gradient(circle at 74% 14%, rgba(127,196,232,0.32) 0 1.4px, transparent 1.5px), radial-gradient(circle at 80% 34%, rgba(139,111,227,0.22) 0 1.8px, transparent 1.9px)",
    opacity: 0.8,
    pointerEvents: "none",
  },
  logoWrap: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    justifyContent: "center",
    marginBottom: 18,
  },
  logo: {
    width: 180,
    height: 180,
    objectFit: "contain",
    filter: "drop-shadow(0 12px 24px rgba(28,24,56,0.16))",
  },
  visualText: {
    position: "relative",
    zIndex: 1,
    textAlign: "center",
  },
  visualTitle: {
    margin: "0 0 10px",
    color: "var(--ink)",
    fontSize: 28,
    letterSpacing: 0.4,
  },
  visualBody: {
    margin: "0 auto",
    maxWidth: 300,
    lineHeight: 1.7,
    fontSize: 14,
    color: "var(--ink-2)",
  },
  screenshots: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
    marginTop: 18,
  },
  screenShot: {
    width: "100%",
    borderRadius: 24,
    border: "1px solid var(--rule-soft)",
    boxShadow: "0 18px 34px rgba(28,24,56,0.14)",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16,
    marginTop: 16,
  },
  infoCard: {
    background: "#fff",
    border: "1px solid var(--rule-soft)",
    borderRadius: 20,
    padding: "18px 18px 16px",
    textAlign: "left",
    boxShadow: "0 10px 28px rgba(28,24,56,0.08)",
  },
  cardEyebrow: {
    margin: "0 0 8px",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "var(--kana)",
    fontWeight: 700,
  },
  cardTitle: {
    margin: "0 0 8px",
    fontSize: 18,
    color: "var(--ink)",
  },
  cardText: {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.7,
    color: "var(--ink-2)",
  },
  footer: {
    marginTop: 42,
    paddingTop: 18,
    borderTop: "1px solid var(--rule-soft)",
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
    alignItems: "center",
    color: "var(--ink-3)",
    fontSize: 13,
  },
};

const KanaTomoLanding: React.FC = () => {
  const [locale, setLocale] = React.useState<Locale>("en");

  const handleStoreClick = React.useCallback((storeName: "app_store" | "google_play") => () => {
    trackStoreClick({
      app_name: "kanatomo",
      store_name: storeName,
      source_page: "kanatomo_landing",
    });
  }, []);

  React.useEffect(() => {
    setLocale(detectLocale());
  }, []);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    }
  }, [locale]);

  const copy = content[locale];

  return (
    <main style={styles.page}>
      <div style={styles.shell}>
        <nav style={styles.nav}>
          <Link to="/" style={styles.navBrand}>
            Daydream Factory
          </Link>
          <div style={styles.navActions}>
            <div style={styles.localeSwitch} aria-label="Language switcher">
              {(Object.keys(localeLabels) as Locale[]).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setLocale(option)}
                  style={{
                    ...styles.localeButton,
                    ...(locale === option ? styles.activeLocaleButton : {}),
                  }}
                >
                  {localeLabels[option]}
                </button>
              ))}
            </div>
            <div style={styles.navLinks}>
              <Link to="/about" style={styles.navLink}>
                {copy.navAbout}
              </Link>
              <Link to="/terms" style={styles.navLink}>
                {copy.navTerms}
              </Link>
              <Link to="/privacy" style={styles.navLink}>
                {copy.navPrivacy}
              </Link>
            </div>
          </div>
        </nav>

        <section style={styles.hero}>
          <div style={styles.heroCopy}>
            <span style={styles.overline}>{copy.overline}</span>
            <h1 style={styles.title}>
              {copy.appName}
              <br />
              {copy.title[0]}
              <br />
              {copy.title[1]}
            </h1>
            <p style={styles.lead}>{copy.lead}</p>

            <div style={styles.buttonRow}>
              <a
                href="https://apps.apple.com/kr/app/kanatomo/id6749524613"
                target="_blank"
                rel="noreferrer"
                onClick={handleStoreClick("app_store")}
                style={styles.primaryButton}
              >
                {copy.appStore}
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=net.daydreamfactory.kanatomo"
                target="_blank"
                rel="noreferrer"
                onClick={handleStoreClick("google_play")}
                style={styles.secondaryButton}
              >
                {copy.googlePlay}
              </a>
            </div>

            <p style={styles.note}>{copy.note}</p>
          </div>

          <div style={styles.heroVisualWrap}>
            <div style={styles.halo} />
            <div style={styles.heroCard}>
              <div style={styles.stars} />
              <div style={styles.logoWrap}>
                <img src={kanaLogo} alt="KanaTomo logo" style={styles.logo} />
              </div>
              <div style={styles.visualText}>
                <h2 style={styles.visualTitle}>Japanese through examples</h2>
                <p style={styles.visualBody}>{copy.visualBody}</p>
              </div>
              <div style={styles.screenshots}>
                <img src={kanaShot1} alt="KanaTomo screenshot 1" style={styles.screenShot} />
                <img src={kanaShot2} alt="KanaTomo screenshot 2" style={styles.screenShot} />
              </div>
            </div>
          </div>
        </section>

        <section style={styles.grid}>
          {copy.cards.map((card) => (
            <article key={card.title} style={styles.infoCard}>
              <p style={styles.cardEyebrow}>{card.eyebrow}</p>
              <h3 style={styles.cardTitle}>{card.title}</h3>
              <p style={styles.cardText}>{card.body}</p>
            </article>
          ))}
        </section>

        <footer style={styles.footer}>
          <span>support@daydreamfactory.net</span>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <Link to="/about" style={styles.navLink}>
              {copy.footerAbout}
            </Link>
            <Link to="/terms" style={styles.navLink}>
              {copy.footerTerms}
            </Link>
            <Link to="/privacy" style={styles.navLink}>
              {copy.footerPrivacy}
            </Link>
          </div>
        </footer>
      </div>
    </main>
  );
};

export default KanaTomoLanding;
